import * as RDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { type ReactElement, type ReactNode, type RefObject } from "react";

import { cn } from "../utils/cn";

export type ModalSize = "sm" | "md" | "lg";

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  /** Show the close button in the header. Default true. */
  showClose?: boolean;
  /** Element to focus when the dialog opens; default: the first focusable one. */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

const SIZES: Record<ModalSize, string> = {
  sm: "max-w-[420px]",
  md: "max-w-[640px]",
  lg: "max-w-[880px]",
};

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
  showClose = true,
  initialFocusRef,
}: ModalProps): ReactElement {
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-neutral-900/50 backdrop-blur-sm",
            "data-[state=open]:animate-in data-[state=open]:fade-in",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out",
          )}
        />
        <RDialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[calc(100%-3rem)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg bg-neutral-0 shadow-xl",
            SIZES[size],
          )}
          aria-describedby={description ? undefined : undefined}
          {...(initialFocusRef && {
            onOpenAutoFocus: (e: Event) => {
              e.preventDefault();
              initialFocusRef.current?.focus();
            },
          })}
        >
          {title && (
            <div className="flex items-center justify-between border-b border-border-default px-5 py-6 sm:px-8">
              <RDialog.Title className="m-0 text-lg font-semibold">{title}</RDialog.Title>
              {showClose && (
                <RDialog.Close
                  aria-label="Schließen"
                  className="inline-flex rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  <X size={20} />
                </RDialog.Close>
              )}
            </div>
          )}
          {description && (
            <RDialog.Description className="px-5 pt-4 text-sm text-text-secondary sm:px-8">
              {description}
            </RDialog.Description>
          )}
          <div className="flex-1 overflow-auto p-5 sm:p-8">{children}</div>
          {footer && (
            // Phones: stacked full width, the primary (last) at the bottom, so the screen
            // follows the focus order (#163).
            <div className="flex flex-wrap justify-end gap-3 border-t border-border-default px-5 py-5 max-sm:flex-col max-sm:*:w-full sm:px-8">
              {footer}
            </div>
          )}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}
